import { token } from '@atlaskit/tokens';
import { type SelectOptionColor } from '@databases/model/field';
import { mapDarkColorToLight } from '@databases/utils/database/field-types/select';

import { paletteColorsMap, type ColorPalette } from './types';

/* eslint-disable @atlaskit/design-system/no-unsafe-design-token-usage -- The lint catalog does not yet include the typed subtle accent border tokens. */

type LozengePaletteColors = {
  backgroundColor: string;
  borderColor: string;
  hoverBackgroundColor: string;
  textColor: string;
};

const lozengeSwatchColors = [
  'greyLight',
  'blueLight',
  'tealLight',
  'greenLight',
  'limeLight',
  'yellowLight',
  'orangeLight',
  'redLight',
  'magentaLight',
  'purpleLight'
] as const satisfies readonly SelectOptionColor[];

type LightSelectOptionColor = (typeof lozengeSwatchColors)[number];

const lozengeTooltipMessages = {
  greyLight: paletteColorsMap.greyDark,
  blueLight: paletteColorsMap.blueDark,
  tealLight: paletteColorsMap.tealDark,
  greenLight: paletteColorsMap.greenDark,
  limeLight: paletteColorsMap.limeDark,
  yellowLight: paletteColorsMap.yellowDark,
  orangeLight: paletteColorsMap.orangeDark,
  redLight: paletteColorsMap.redDark,
  magentaLight: paletteColorsMap.magentaDark,
  purpleLight: paletteColorsMap.purpleDark
} satisfies Record<LightSelectOptionColor, (typeof paletteColorsMap)[string]>;

const lozengePaletteColors = {
  greyLight: {
    backgroundColor: token('color.background.accent.gray.subtlest'),
    borderColor: token('color.border'),
    hoverBackgroundColor: token('color.background.accent.gray.subtlest.hovered'),
    textColor: token('color.text.accent.gray.bolder')
  },
  blueLight: {
    backgroundColor: token('color.background.accent.blue.subtler'),
    borderColor: token('color.border.accent.blue.subtle'),
    hoverBackgroundColor: token('color.background.accent.blue.subtler.hovered'),
    textColor: token('color.text.accent.blue.bolder')
  },
  tealLight: {
    backgroundColor: token('color.background.accent.teal.subtler'),
    borderColor: token('color.border.accent.teal.subtle'),
    hoverBackgroundColor: token('color.background.accent.teal.subtler.hovered'),
    textColor: token('color.text.accent.teal.bolder')
  },
  greenLight: {
    backgroundColor: token('color.background.accent.green.subtler'),
    borderColor: token('color.border.accent.green.subtle'),
    hoverBackgroundColor: token('color.background.accent.green.subtler.hovered'),
    textColor: token('color.text.accent.green.bolder')
  },
  limeLight: {
    backgroundColor: token('color.background.accent.lime.subtler'),
    borderColor: token('color.border.accent.lime.subtle'),
    hoverBackgroundColor: token('color.background.accent.lime.subtler.hovered'),
    textColor: token('color.text.accent.lime.bolder')
  },
  yellowLight: {
    backgroundColor: token('color.background.accent.yellow.subtler'),
    borderColor: token('color.border.accent.yellow.subtle'),
    hoverBackgroundColor: token('color.background.accent.yellow.subtler.hovered'),
    textColor: token('color.text.accent.yellow.bolder')
  },
  orangeLight: {
    backgroundColor: token('color.background.accent.orange.subtler'),
    borderColor: token('color.border.accent.orange.subtle'),
    hoverBackgroundColor: token('color.background.accent.orange.subtler.hovered'),
    textColor: token('color.text.accent.orange.bolder')
  },
  redLight: {
    backgroundColor: token('color.background.accent.red.subtler'),
    borderColor: token('color.border.accent.red.subtle'),
    hoverBackgroundColor: token('color.background.accent.red.subtler.hovered'),
    textColor: token('color.text.accent.red.bolder')
  },
  magentaLight: {
    backgroundColor: token('color.background.accent.magenta.subtler'),
    borderColor: token('color.border.accent.magenta.subtle'),
    hoverBackgroundColor: token('color.background.accent.magenta.subtler.hovered'),
    textColor: token('color.text.accent.magenta.bolder')
  },
  purpleLight: {
    backgroundColor: token('color.background.accent.purple.subtler'),
    borderColor: token('color.border.accent.purple.subtle'),
    hoverBackgroundColor: token('color.background.accent.purple.subtler.hovered'),
    textColor: token('color.text.accent.purple.bolder')
  }
} satisfies Record<LightSelectOptionColor, LozengePaletteColors>;

const getPaletteColors = (color: SelectOptionColor): LozengePaletteColors =>
  lozengePaletteColors[mapDarkColorToLight(color) as LightSelectOptionColor];

export const TagColorPaletteLozenge: ColorPalette<SelectOptionColor> = {
  backgroundColor: (color) => getPaletteColors(color).backgroundColor,
  borderColor: (color) => getPaletteColors(color).borderColor,
  hoverBackgroundColor: (color) => getPaletteColors(color).hoverBackgroundColor,
  textColor: (color) => getPaletteColors(color).textColor,
  tooltipMessage: (color) =>
    lozengeTooltipMessages[mapDarkColorToLight(color) as LightSelectOptionColor],
  swatch: () => lozengeSwatchColors
};
